/**
 * The /help illustrations — one small stateless component per section.
 *
 * Each is a simplified replica of the real screen built from the `hp-*` mock primitives in
 * help.scss, animated by the seven shared keyframes there (see the motion contract at the top of
 * that file: the resting stylesheet state IS the final frame). The frame around them is
 * `aria-hidden`, so nothing here is a real <button> or <a>, and identity-bearing labels only —
 * names, points, times, counts — are real text; everything descriptive is an `hp-bar`.
 *
 * The data is a FICTIONAL company (Marigold Trading: networks Bluefinch and Okapi; merchandisers
 * Ana Voss, Leo Mard, Nika Vale; supervisors Olena Brant and Davit Aren), deliberately Latin-script
 * and untranslated: the copy beside each picture, which is translated, says what it shows.
 */
import React from "react";
import { Avatar } from "antd";
import {
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DownOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  InboxOutlined,
  LeftOutlined,
  LogoutOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SearchOutlined,
  StarOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { initialsOf } from "../../component/table-cells";

const cx = (...parts) => parts.filter(Boolean).join(" ");

const TONE_ICON = { ok: CheckCircleOutlined, no: CloseCircleOutlined, wait: ClockCircleOutlined, off: MinusCircleOutlined };

/** The house status glyph (same `lp-*` classes the real tables use), without the Tooltip. */
const Glyph = ({ tone, extra }) => {
  const Icon = TONE_ICON[tone];
  return <Icon className={cx("lp-status-icon", `lp-status-icon--${tone}`, extra)} />;
};

/** A glyph that flips at the click: `from` is drawn only before it, `to` is the resting state. */
const Flip = ({ from, to }) => (
  <span className="hp-flip">
    <Glyph tone={from} extra="hp-flip__before" />
    <Glyph tone={to} extra="hp-flip__after" />
  </span>
);

const Av = ({ name }) => (
  <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }}>
    {initialsOf(name)}
  </Avatar>
);

const Bar = ({ w = "60%" }) => <span className="hp-bar" style={{ "--w": w }} />;

/** The fake pointer. `x`/`y` are where it comes to rest (the click), `dx`/`dy` where it starts from. */
export const Cursor = ({ x, y, dx = "120px", dy = "80px" }) => (
  <span className="hp-cursor" style={{ "--x": x, "--y": y, "--dx": dx, "--dy": dy }}>
    <svg viewBox="0 0 16 16">
      <path d="M2 2 14 8 8 9.5 6 15z" />
    </svg>
  </span>
);

function Row({ lead, who, where, tail, target, appear }) {
  return (
    <div className={cx("hp-row", target && "hp-row--target", appear && "hp-appear")}>
      {lead}
      <span className="hp-row__main">
        <strong>{who}</strong>
        {where ? <span>{where}</span> : null}
      </span>
      <span className="hp-row__tail">{tail}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ shell */

export function SearchIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-search">
        <SearchOutlined className="hp-search__icon" />
        <span>
          <span className="hp-type--1">b</span>
          <span className="hp-type--2">l</span>
          <span className="hp-type--3">u</span>
        </span>
        <span className="hp-key">/</span>
      </div>
      <div className="hp-glabel hp-appear">
        <Bar w="48px" /> · 2
      </div>
      <Row lead={<span className="hp-tile">BF</span>} who="Bluefinch 12" where="Okapi Park · Bluefinch" tail={<RightOutlined />} appear />
      <Row lead={<span className="hp-tile">BF</span>} who="Bluefinch Central" where="Bluefinch" tail={<RightOutlined />} appear />
    </div>
  );
}

export function NotificationsIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar hp-mock__bar--end">
        <Bar w="40%" />
        <span className="hp-spacer" />
        <span className="hp-bell">
          <BellOutlined />
          <span className="hp-bell__dot hp-flip__before" />
        </span>
        <span className="hp-userchip">
          <Av name="Olena Brant" />
          <Bar w="56px" />
        </span>
      </div>
      <div className="hp-menu" style={{ width: "62%" }}>
        <div className="hp-menu__row">
          <Glyph tone="wait" />
          <Bar w="55%" />
          <span className="hp-spacer" />
          <span className="hp-chip hp-chip--warn">3</span>
        </div>
        <div className="hp-menu__row">
          <UserOutlined />
          <Bar w="45%" />
          <span className="hp-spacer" />
          <span className="hp-chip">2</span>
        </div>
        <div className="hp-menu__row">
          <CalendarOutlined />
          <Bar w="50%" />
          <span className="hp-spacer" />
          <span className="hp-chip">1</span>
        </div>
      </div>
      <Cursor x="calc(100% - 118px)" y="14px" dx="-160px" dy="90px" />
    </div>
  );
}

export function AccountIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar hp-mock__bar--end">
        <Bar w="40%" />
        <span className="hp-spacer" />
        <span className="hp-btn hp-btn--ghost">
          EN <DownOutlined />
        </span>
        <span className="hp-userchip">
          <Av name="Olena Brant" />
          <span className="hp-row__main">
            <strong>Olena Brant</strong>
            <Bar w="48px" />
          </span>
          <DownOutlined />
        </span>
      </div>
      <div className="hp-menu">
        <div className="hp-menu__row">
          <Av name="Olena Brant" />
          <span className="hp-row__main">
            <strong>Olena Brant</strong>
            <Bar w="56px" />
          </span>
        </div>
        <div className="hp-menu__row">
          <UserOutlined />
          <Bar w="40%" />
        </div>
        <div className="hp-menu__row">
          <QuestionCircleOutlined />
          <Bar w="30%" />
        </div>
        <div className="hp-menu__row">
          <LogoutOutlined />
          <Bar w="24%" />
        </div>
      </div>
      <Cursor x="calc(100% - 60px)" y="16px" dx="-180px" dy="80px" />
    </div>
  );
}

/* ------------------------------------------------------------------ lists */

export function ListsIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-input" style={{ width: "34%" }}>
          Bluefinch
          <span className="hp-spacer" />
          <DownOutlined />
        </span>
        <span className="hp-input" style={{ width: "30%" }}>
          <Bar w="70%" />
        </span>
        <span className="hp-input">
          <CalendarOutlined />
          &nbsp;01.08 → 14.08
        </span>
      </div>
      <div className="hp-mock__bar">
        <span className="hp-chip hp-chip--on hp-appear">
          Bluefinch <CloseOutlined />
        </span>
        <span className="hp-spacer" />
        <span>· 24</span>
      </div>
      <Row lead={<Glyph tone="ok" />} who="Bluefinch 12" where="Ana Voss · 12:05" tail={<Bar w="32px" />} appear />
      <Row lead={<Glyph tone="wait" />} who="Bluefinch Central" where="Leo Mard · 12:40" tail={<Bar w="32px" />} appear />
      <Row lead={<Glyph tone="ok" />} who="Bluefinch Station" where="Nika Vale · 13:10" tail={<Bar w="32px" />} appear />
      <Cursor x="26%" y="18px" dx="140px" dy="110px" />
    </div>
  );
}

/* ------------------------------------------------------------------ pages */

export function OverviewIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <Bar w="30%" />
        <span className="hp-spacer" />
        <span className="hp-key">
          <LeftOutlined />
        </span>
        <span>
          <span className="hp-flip">
            <span className="hp-flip__before">Jul 2026</span>
            <span className="hp-flip__after">Aug 2026</span>
          </span>
        </span>
        <span className="hp-key">
          <RightOutlined />
        </span>
      </div>
      <div className="hp-kpis">
        {[
          ["128", "141", "+10%"],
          ["36", "41", "+14%"],
          ["512", "590", "+15%"],
        ].map(([a, b, d]) => (
          <div className="hp-kpi" key={a}>
            <Bar w="50%" />
            <span className="hp-kpi__n hp-flip">
              <span className="hp-flip__before">{a}</span>
              <span className="hp-flip__after">{b}</span>
            </span>
            <span className="hp-chip hp-chip--ok hp-appear">{d}</span>
          </div>
        ))}
      </div>
      <div className="hp-chart">
        {["40%", "65%", "55%", "80%", "70%", "95%", "60%"].map((h, i) => (
          <span className="hp-chart__bar" style={{ "--h": h }} key={i} />
        ))}
      </div>
      <Cursor x="calc(100% - 22px)" y="14px" dx="-120px" dy="100px" />
    </div>
  );
}

export function PhotoReviewIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-chip hp-chip--on">
          <Bar w="32px" />
        </span>
        <span className="hp-chip">
          <Bar w="40px" />
        </span>
        <span className="hp-chip">
          <Bar w="36px" />
        </span>
      </div>
      <div className="hp-photos">
        {["ok", "wait", "wait", "ok", "no", "wait"].map((tone, i) => (
          <span className="hp-photo" key={i}>
            <CameraOutlined className="hp-photo__icon" />
            {i === 1 ? <Flip from="wait" to="ok" /> : <Glyph tone={tone} />}
          </span>
        ))}
      </div>
      <div className="hp-float">
        <span className="hp-photo hp-photo--big">
          <CameraOutlined className="hp-photo__icon" />
        </span>
        <span className="hp-row__main">
          <strong>Okapi Park</strong>
          <span>Leo Mard · 12:40</span>
        </span>
        <div className="hp-float__acts">
          <span className="hp-btn hp-btn--ghost">
            <CheckOutlined />
          </span>
          <span className="hp-btn hp-btn--danger">
            <CloseOutlined />
          </span>
        </div>
      </div>
      <Cursor x="42%" y="70px" dx="-80px" dy="90px" />
    </div>
  );
}

export function VisitsIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-chip hp-chip--on">
          <Bar w="28px" />
        </span>
        <span className="hp-chip">
          <Bar w="44px" />
        </span>
        <span className="hp-chip">
          <Bar w="36px" />
        </span>
        <span className="hp-spacer" />
        <span>· 84</span>
      </div>
      <Row lead={<Av name="Ana Voss" />} who="Ana Voss" where="Bluefinch 12 · 12:05" tail={<Glyph tone="ok" />} />
      <Row lead={<Av name="Leo Mard" />} who="Leo Mard" where="Okapi Park · 12:40" tail={<Flip from="wait" to="ok" />} target />
      <Row lead={<Av name="Nika Vale" />} who="Nika Vale" where="Bluefinch Central · 13:10" tail={<Glyph tone="wait" />} />
      <Cursor x="calc(100% - 26px)" y="86px" dx="-160px" dy="70px" />
    </div>
  );
}

export function TrackingIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-cols hp-cols--rail">
        <div className="hp-panel">
          <Row lead={<Av name="Ana Voss" />} who="Ana Voss" where="Bluefinch 12 · 12:05" tail={<Glyph tone="ok" />} target />
          <Row lead={<Av name="Leo Mard" />} who="Leo Mard" where="Okapi Park · 08:10" tail={<Glyph tone="wait" />} />
          <Row lead={<Av name="Nika Vale" />} who="Nika Vale" where="Bluefinch Central · 11:20" tail={<Glyph tone="off" />} />
        </div>
        <div className="hp-map">
          <svg className="hp-track" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M14 78 L38 56 L62 30" />
          </svg>
          <span className="hp-pin hp-pin--off" style={{ "--x": "10%", "--y": "72%" }} />
          <span className="hp-pin hp-pin--ok" style={{ "--x": "36%", "--y": "50%" }} />
          <span className="hp-pin hp-pin--off" style={{ "--x": "78%", "--y": "60%" }} />
          <span className="hp-pin hp-pin--here hp-pin--ok" style={{ "--x": "58%", "--y": "22%" }} />
        </div>
      </div>
    </div>
  );
}

export function BiReportsIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-accordion">
        <Row lead={<BarChartOutlined />} who="Sales by network" tail={<DownOutlined />} />
        <Row lead={<BarChartOutlined />} who="Visit coverage" tail={<DownOutlined />} target />
      </div>
      <div className="hp-frame hp-appear">
        <div className="hp-chart">
          {["50%", "80%", "35%", "95%", "60%"].map((h, i) => (
            <span className="hp-chart__bar" style={{ "--h": h }} key={i} />
          ))}
        </div>
      </div>
      <Row lead={<BarChartOutlined />} who="Shelf share" tail={<DownOutlined />} />
      <Cursor x="calc(100% - 24px)" y="52px" dx="-140px" dy="80px" />
    </div>
  );
}

export function TasksIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <Bar w="30%" />
        <span className="hp-spacer" />
        <span className="hp-btn">
          <PlusOutlined />
          <Bar w="40px" />
        </span>
      </div>
      <Row lead={<Glyph tone="ok" />} who="Okapi Park" where="Ana Voss · 28.08" tail={<Bar w="28px" />} />
      <Row lead={<Glyph tone="wait" />} who="Bluefinch 12" where="Leo Mard · 04.09" tail={<Bar w="28px" />} appear />
      <Row lead={<Glyph tone="wait" />} who="Bluefinch Central" where="Nika Vale · 04.09" tail={<Bar w="28px" />} appear />
      <Row lead={<Glyph tone="wait" />} who="Bluefinch Station" where="Nika Vale · 04.09" tail={<Bar w="28px" />} appear />
      <Cursor x="calc(100% - 44px)" y="16px" dx="-160px" dy="100px" />
    </div>
  );
}

export function AnswersIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <Bar w="24%" />
        <span className="hp-spacer" />
        <span className="hp-key">
          <LeftOutlined />
        </span>
        <span className="hp-flip">
          <span className="hp-flip__before">3 / 84</span>
          <span className="hp-flip__after">4 / 84</span>
        </span>
        <span className="hp-key">
          <RightOutlined />
        </span>
      </div>
      <div className="hp-cols">
        <span className="hp-photo hp-photo--big">
          <CameraOutlined className="hp-photo__icon" />
        </span>
        <div className="hp-col">
          <span className="hp-row__main">
            <strong>Okapi Riverside</strong>
            <span>Nika Vale · 13:10</span>
          </span>
          <Bar w="90%" />
          <Bar w="70%" />
          <div className="hp-float__acts">
            <span className="hp-btn hp-btn--ghost">
              <Flip from="wait" to="ok" />
            </span>
            <span className="hp-btn hp-btn--danger">
              <CloseOutlined />
            </span>
          </div>
        </div>
      </div>
      <Cursor x="calc(50% + 30px)" y="calc(100% - 30px)" dx="-120px" dy="-70px" />
    </div>
  );
}

export function ImportIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-drop">
        <InboxOutlined />
        <Bar w="40%" />
        <span className="hp-spacer" />
        <span className="hp-chip hp-chip--on hp-appear">
          <FileExcelOutlined /> tasks_09.xlsx
        </span>
      </div>
      <Row lead={<Glyph tone="ok" />} who="Bluefinch 12" where="04.09 · Facing check" tail={<Bar w="28px" />} appear />
      <Row lead={<Glyph tone="ok" />} who="Okapi Park" where="04.09 · Facing check" tail={<Bar w="28px" />} appear />
      <Row
        lead={<Glyph tone="no" />}
        who="BLF-0199"
        where="31.02 · Facing check"
        tail={<WarningOutlined className="lp-status-icon lp-status-icon--no" />}
        appear
      />
      <Cursor x="30%" y="28px" dx="100px" dy="100px" />
    </div>
  );
}

export function MerchandisersIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-chip">
          <CalendarOutlined />
        </span>
        <span className="hp-chip">
          <StarOutlined />
        </span>
        <span className="hp-chip">
          <EnvironmentOutlined />
        </span>
      </div>
      <div className="hp-cols hp-cols--rail" style={{ paddingTop: 0 }}>
        <div className="hp-panel">
          <Row lead={<Av name="Ana Voss" />} who="Ana Voss" where="Olena Brant" tail={<Glyph tone="ok" />} />
          <Row lead={<Av name="Leo Mard" />} who="Leo Mard" where="Olena Brant" tail={<StarOutlined />} target />
          <Row lead={<Av name="Nika Vale" />} who="Nika Vale" where="Davit Aren" tail={<Glyph tone="off" />} />
        </div>
        <div className="hp-col hp-appear">
          <div className="hp-tabs">
            <span className="hp-tabs__on">
              <Bar w="40px" />
            </span>
            <span>
              <Bar w="32px" />
            </span>
            <span>
              <Bar w="28px" />
            </span>
          </div>
          <div className="hp-cal">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(d => (
              <span className={cx("hp-cal__d", [2, 4, 9, 11, 12].includes(d) && "hp-cal__d--on")} key={d}>
                {d}
              </span>
            ))}
          </div>
        </div>
      </div>
      <Cursor x="22%" y="96px" dx="120px" dy="-60px" />
    </div>
  );
}

export function SupervisorsIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-cols hp-cols--rail">
        <div className="hp-panel">
          <Row lead={<Av name="Olena Brant" />} who="Olena Brant" where="Points: 24" tail={<Glyph tone="ok" />} target />
          <Row lead={<Av name="Davit Aren" />} who="Davit Aren" where="Points: 0" tail={<WarningOutlined className="lp-status-icon lp-status-icon--wait" />} />
        </div>
        <div className="hp-col">
          <div className="hp-tabs">
            <span className="hp-tabs__on">
              <EnvironmentOutlined /> 24
            </span>
            <span>
              <TeamOutlined /> 3
            </span>
            <span className="hp-spacer" />
            <span className="hp-btn" style={{ alignSelf: "center" }}>
              <PlusOutlined />
            </span>
          </div>
          <span className="hp-chip">Bluefinch 12</span>
          <span className="hp-chip">Okapi Park</span>
          <span className="hp-chip hp-chip--on hp-appear">Okapi Riverside</span>
        </div>
      </div>
      <Cursor x="calc(100% - 30px)" y="26px" dx="-140px" dy="90px" />
    </div>
  );
}

export function DocumentsIllo() {
  const open = <ExportOutlined className="lp-status-icon lp-status-icon--off" />;
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-input" style={{ width: "40%" }}>
          <SearchOutlined className="hp-search__icon" />
        </span>
        <span className="hp-spacer" />
        <span className="hp-btn">
          <PlusOutlined />
          <Bar w="36px" />
        </span>
      </div>
      <Row lead={<FilePdfOutlined className="hp-photo__icon" />} who="Planogram Q3" where="PDF · 12.08" tail={open} />
      <Row lead={<FileExcelOutlined className="hp-photo__icon" />} who="Price list 09" where="XLSX · 28.08" tail={open} target />
      <Row lead={<FileImageOutlined className="hp-photo__icon" />} who="Shelf standard" where="PNG · 02.09" tail={open} />
      <Cursor x="calc(100% - 22px)" y="92px" dx="-160px" dy="-40px" />
    </div>
  );
}

export function SurveysIllo() {
  return (
    <div className="hp-mock">
      <Row lead={<span className="hp-tile">1</span>} who="Shelf share, %" tail={<DownOutlined />} />
      <Row lead={<span className="hp-tile">2</span>} who="Promo material present?" tail={<DownOutlined />} target />
      <div className="hp-frame hp-appear">
        <div className="hp-col" style={{ padding: 12, flex: 1 }}>
          <span className="hp-input">
            <Bar w="60%" />
          </span>
          <div className="hp-float__acts" style={{ marginTop: 0 }}>
            <span className="hp-chip hp-chip--on">Bluefinch</span>
            <span className="hp-chip">Okapi</span>
          </div>
        </div>
      </div>
      <Row lead={<span className="hp-tile">3</span>} who="Competitor price" tail={<DownOutlined />} />
      <Cursor x="calc(100% - 24px)" y="52px" dx="-140px" dy="90px" />
    </div>
  );
}

export function ReferenceIllo() {
  return (
    <div className="hp-mock">
      <div className="hp-mock__bar">
        <span className="hp-chip">
          <EnvironmentOutlined />
        </span>
        <span className="hp-chip">
          <UserOutlined />
        </span>
        <span className="hp-spacer" />
        <span className="hp-btn">
          <PlusOutlined />
          <Bar w="32px" />
        </span>
      </div>
      <Row lead={<span className="hp-tile">BF</span>} who="Bluefinch 12" where="Ana Voss" tail={<Glyph tone="ok" />} />
      <Row lead={<span className="hp-tile">OK</span>} who="Okapi Park" where="Leo Mard" tail={<Glyph tone="ok" />} />
      <Row
        lead={<span className="hp-tile">OK</span>}
        who="Okapi Riverside"
        where="—"
        tail={<WarningOutlined className="lp-status-icon lp-status-icon--wait" />}
      />
      <div className="hp-float hp-float--below">
        <span className="hp-input">
          <Bar w="55%" />
        </span>
        <span className="hp-input">
          <Bar w="40%" />
        </span>
        <span className="hp-map">
          <span className="hp-pin hp-pin--here" style={{ "--x": "46%", "--y": "38%" }} />
        </span>
      </div>
      <Cursor x="calc(100% - 40px)" y="16px" dx="-160px" dy="90px" />
    </div>
  );
}
